/**
 * Persistent storage management
 *
 * Wraps the StorageManager persistence APIs (`navigator.storage.persisted()` /
 * `navigator.storage.persist()`) so callers can request durable storage without
 * worrying about environments where the API is missing or unusable.
 */

/**
 * Check and request persistent storage from the browser.
 * This prevents the browser from evicting data under storage pressure.
 *
 * The Storage API can be present but unusable in some contexts (notably iOS
 * Safari in private mode or with partitioned/blocked storage), where calling
 * `persisted()`/`persist()` throws a TypeError ("Context not access storage").
 * Guard for the methods' existence and swallow any error so this fire-and-forget
 * boot call never produces an unhandled rejection.
 *
 * @returns whether storage is persistent (false when the API is unavailable or throws)
 */
export async function checkAndRequestPersistentStorage(): Promise<boolean> {
  if (typeof navigator.storage?.persisted !== "function") {
    return false;
  }

  try {
    // Check if storage is already persistent
    const isPersisted = await navigator.storage.persisted();
    if (isPersisted) {
      return true;
    }

    // Request persistence if not already granted
    if (typeof navigator.storage.persist === "function") {
      return await navigator.storage.persist();
    }
    return false;
  } catch {
    // iOS Safari can throw when storage access is blocked/partitioned; ignore.
    return false;
  }
}
